<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="flex flex-wrap justify-between items-end gap-3 mb-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">{{ $t('smm.marketing_plans.title') }}</h2>
        <p class="text-sm text-gray-500 mt-1">{{ $t('smm.marketing_plans.subtitle') }}</p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <select v-model="genMonth" class="border rounded-lg px-3 py-2 text-sm bg-white">
          <option v-for="m in 12" :key="m" :value="m">{{ monthLabel(m) }}</option>
        </select>
        <select v-model="genYear" class="border rounded-lg px-3 py-2 text-sm bg-white">
          <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>
        <button
          type="button"
          class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
          :disabled="generating"
          @click="generatePlan"
        >
          {{ generating ? $t('common.saving') : $t('smm.marketing_plans.generate') }}
        </button>
      </div>
    </div>

    <p v-if="error" class="text-sm text-red-600 mb-3">{{ error }}</p>

    <div v-if="loading" class="p-8 text-center text-gray-500">{{ $t('common.loading') }}</div>
    <div v-else-if="!plans.length" class="p-8 text-center text-gray-500 bg-white rounded-lg shadow-md">
      {{ $t('smm.marketing_plans.empty') }}
    </div>
    <div v-else class="space-y-3">
      <article
        v-for="plan in plans"
        :key="plan.id"
        class="bg-white rounded-lg shadow-md border border-gray-100 p-4"
      >
        <div class="flex flex-wrap justify-between gap-3">
          <div>
            <h3 class="font-semibold text-gray-800 text-lg">
              {{ plan.title || plan.period_start }}
            </h3>
            <p class="text-sm text-gray-500 mt-0.5">
              {{ formatDate(plan.period_start) }} – {{ formatDate(plan.period_end) }}
              · {{ $t(`smm.marketing_plans.status.${plan.status}`) }}
              · {{ $t('smm.marketing_plans.tasks_count', { n: plan.tasks_count ?? 0 }) }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
              @click="togglePlan(plan.id)"
            >
              {{ expandedId === plan.id ? $t('common.hide') : $t('common.details') }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              :disabled="schedulingId === plan.id"
              @click="generateSchedule(plan)"
            >
              {{ schedulingId === plan.id ? $t('common.saving') : $t('smm.marketing_plans.generate_schedule') }}
            </button>
            <router-link
              :to="{ name: 'SmmToday' }"
              class="px-3 py-1.5 text-sm rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              {{ $t('smm.marketing_plans.open_today') }}
            </router-link>
          </div>
        </div>

        <p v-if="plan.summary" class="text-sm text-gray-700 mt-3 whitespace-pre-line">{{ plan.summary }}</p>

        <div v-if="expandedId === plan.id" class="mt-4 border-t border-gray-100 pt-3 space-y-3">
          <div v-if="plan.platform_targets" class="text-sm">
            <p class="font-medium text-gray-700 mb-1">{{ $t('smm.marketing_plans.platform_targets') }}</p>
            <pre class="bg-gray-50 rounded-lg p-3 overflow-x-auto text-xs">{{ pretty(plan.platform_targets) }}</pre>
          </div>
          <div v-if="plan.priority_product_ids?.length" class="text-sm text-gray-600">
            {{ $t('smm.marketing_plans.priority_products') }}:
            {{ plan.priority_product_ids.join(', ') }}
          </div>
          <p v-if="plan.error_message" class="text-sm text-red-600">{{ plan.error_message }}</p>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'

export default {
  components: { SmmSubNav },
  data: () => ({
    plans: [],
    loading: true,
    generating: false,
    schedulingId: null,
    expandedId: null,
    error: null,
    genMonth: new Date().getMonth() + 1,
    genYear: new Date().getFullYear(),
  }),
  computed: {
    years() {
      const y = new Date().getFullYear()
      return [y - 1, y, y + 1]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    monthLabel(m) {
      return new Date(2000, m - 1, 1).toLocaleString(this.$i18n.locale === 'ru' ? 'ru-RU' : 'en-US', { month: 'long' })
    },
    formatDate(v) {
      if (!v) return ''
      return String(v).slice(0, 10)
    },
    pretty(v) {
      try {
        return JSON.stringify(v, null, 2)
      } catch (_) {
        return String(v)
      }
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get('/api/v1/smm/marketing-plans')
        this.plans = data.data ?? []
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('common.error')
      } finally {
        this.loading = false
      }
    },
    togglePlan(id) {
      this.expandedId = this.expandedId === id ? null : id
    },
    async generatePlan() {
      this.generating = true
      this.error = null
      try {
        await axios.post('/api/v1/smm/marketing-plans/generate', {
          year: this.genYear,
          month: this.genMonth,
        })
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('common.error')
      } finally {
        this.generating = false
      }
    },
    async generateSchedule(plan) {
      if (!confirm(this.$t('smm.marketing_plans.schedule_confirm'))) return
      this.schedulingId = plan.id
      this.error = null
      try {
        const { data } = await axios.post(`/api/v1/smm/marketing-plans/${plan.id}/generate-schedule`, {
          replace_existing: true,
        })
        alert(this.$t('smm.marketing_plans.schedule_done', {
          n: data.data?.tasks_created ?? 0,
          target: data.data?.stats?.month_target_total ?? '—',
          parsed: data.data?.stats?.parsed ?? '—',
        }))
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('common.error')
      } finally {
        this.schedulingId = null
      }
    },
  },
}
</script>
